import { useCallback, useMemo } from "react"
import {
  type Query as TCubeQuery,
  type TimeDimensionGranularity,
} from "@cubejs-client/core"
import {
  type BucketGranularity,
  comparisonShiftForWindows,
  resolveComparisonDate,
  resolveWeekStartDay,
  toFiniteNumber,
} from "~/shared/kpi"
import { useCompanyControllerGetFinancialsQuery } from "~/ui-rtk/api/companyApi"
import {
  AggregationType,
  type MetricDefinition,
} from "~/ui-rtk/constants/charts"
import type { RegionalCountry } from "~/ui-rtk/constants/regional"
import { useCubeAvailability, useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import useDate from "~/ui-rtk/hooks/date"
import { resolveTimeDimensionKey } from "~/ui-rtk/utils/cube"
import type { KpiConnectParams } from "./connect"
import {
  REGIONAL_COUNTRY_DIMENSION,
  REGIONAL_CUBE,
  REGIONAL_DATE_DIMENSION,
  REGIONAL_KPI_MEASURES,
} from "./constants/regional"
import {
  buildKpiWindows,
  type KpiWindows,
  weeklyBucketCount,
} from "./utils/kpi-compare-date-range"
import { mergeCompareWindows } from "./utils/merge-compare-windows"
import { partialBucketNote } from "./utils/partial-bucket-note"
import {
  getWindowComparisonKey,
  getYoyComparisonKey,
  type YoyGranularity,
  type YoyWindow,
} from "./yoy-comparison"

// Data path for the Regional view: ONE compareDateRange query against the
// regional cube, filtered to a single country, merged into the same
// week_N / week_N_yoy_previous / week_N_yoy_change rows `KPITable` renders for
// the dashboard. It reuses the dashboard's window builder, bucket pairing and
// merge (so a range picked in the shared header picker, the YoY week pairing
// and the weekly 52-bucket trim all behave identically) but stays a separate
// hook: the dashboard's `useConnect` pipeline is untouched.

const GRANULARITY_SUFFIX: Record<AggregationType, BucketGranularity> = {
  [AggregationType.DAILY]: "day",
  [AggregationType.WEEKLY]: "week",
  [AggregationType.MONTHLY]: "month",
}

const DATE_FORMAT: Record<AggregationType, string> = {
  [AggregationType.DAILY]: "MMM DD YYYY",
  [AggregationType.WEEKLY]: "MMM DD YYYY",
  [AggregationType.MONTHLY]: "MMM YYYY",
}

export type RegionalKpiRow = Record<string, string | number | null | undefined>

export interface RegionalKpiColumn {
  key: string
  header: string
  type: string
  /** Set on an in-progress bucket: what it covers so far and its YoY twin. */
  note?: string
}

const METRIC_COLUMN: RegionalKpiColumn = {
  key: "metric",
  header: "Metric",
  type: "title",
}

const byDate =
  (dateKey: string) =>
  (a: Record<string, unknown>, b: Record<string, unknown>) =>
    new Date(a[dateKey] as string).getTime() -
    new Date(b[dateKey] as string).getTime()

/** The regional cube query for one country over the shared KPI windows. */
export function buildRegionalQuery(
  country: RegionalCountry,
  granularity: AggregationType,
  windows: KpiWindows,
): TCubeQuery {
  return {
    measures: REGIONAL_KPI_MEASURES,
    filters: [
      {
        member: REGIONAL_COUNTRY_DIMENSION,
        operator: "equals",
        values: [country],
      },
    ],
    timeDimensions: [
      {
        dimension: REGIONAL_DATE_DIMENSION,
        granularity: GRANULARITY_SUFFIX[
          granularity
        ] as TimeDimensionGranularity,
        // [comparison, current]: loadResponses[1] is the current window.
        compareDateRange: [windows.comparisonRange, windows.currentRange],
      },
    ],
  }
}

// The weekly result key may come back rewritten to the company's custom week
// granularity (e.g. `….date.week_sunday`), so resolve it from the rows.
export function resolveRegionalDateKey(
  rows: Array<Record<string, unknown>> | null | undefined,
  granularity: AggregationType,
): string {
  return resolveTimeDimensionKey(
    rows,
    REGIONAL_DATE_DIMENSION,
    `${REGIONAL_DATE_DIMENSION}.${GRANULARITY_SUFFIX[granularity]}`,
  )
}

/**
 * One table row per metric: bucket values plus their YoY twins, shaped exactly
 * like the dashboard's `processMetrics` output. Cube delivers measures as
 * strings, hence `toFiniteNumber` before any arithmetic.
 */
export function processRegionalMetrics(
  data: Array<Record<string, unknown>> | null,
  dateKey: string,
  metrics: MetricDefinition[],
): RegionalKpiRow[] {
  if (!data || data.length === 0) return []

  const sortedData = [...data].sort(byDate(dateKey))

  return metrics.map(metricDef => {
    const row: RegionalKpiRow = {
      metric: metricDef.label,
      type: metricDef.type,
      key: metricDef.key,
      suffix: metricDef.suffix,
    }

    sortedData.forEach((bucket, index) => {
      const weekKey = `week_${index + 1}`
      const currentValue = toFiniteNumber(bucket[metricDef.key])
      const previousValue = toFiniteNumber(bucket[`compared.${metricDef.key}`])

      let periodChange: number | null = null
      if (previousValue !== 0) {
        periodChange = ((currentValue - previousValue) / previousValue) * 100
      } else if (currentValue === 0) {
        periodChange = 0
      }

      row[weekKey] = currentValue
      row[`${weekKey}_yoy_previous`] = previousValue
      row[`${weekKey}_yoy_change`] = periodChange
    })

    return row
  })
}

/**
 * The "Metric" title column plus one dated column per bucket, in the order the
 * row builder numbers them. The newest bucket carries the dashboard's
 * partial-period note when it is still in progress.
 */
export function buildRegionalColumns(
  data: Array<Record<string, unknown>> | null,
  dateKey: string,
  granularity: AggregationType,
  dayjs: ReturnType<typeof useDate>,
  comparisonStartOf?: (bucketStart: string) => string,
): RegionalKpiColumn[] {
  if (!data || data.length === 0) return [METRIC_COLUMN]

  const sortedData = [...data].sort(byDate(dateKey))
  const gran = GRANULARITY_SUFFIX[granularity]

  return [
    METRIC_COLUMN,
    ...sortedData.map((bucket, index) => {
      const bucketStart = String(bucket[dateKey] ?? "").slice(0, 10)
      const note =
        index === sortedData.length - 1 && bucketStart.length === 10
          ? partialBucketNote(
              bucketStart,
              resolveComparisonDate(
                bucket,
                dateKey,
                bucketStart,
                gran,
                comparisonStartOf,
              ),
              gran,
            )
          : null
      return {
        key: `week_${index + 1}`,
        header: dayjs(bucket[dateKey] as string).format(
          DATE_FORMAT[granularity],
        ),
        type: "date",
        ...(note ? { note } : {}),
      }
    }),
  ]
}

export default function useRegionalConnect(
  country: RegionalCountry,
  granularity: AggregationType = AggregationType.WEEKLY,
  params?: KpiConnectParams,
) {
  const dayjs = useDate()

  // Week start day only drives the YoY week pairing; the result-row date key
  // is resolved from the response itself (resolveRegionalDateKey).
  const currentCompany = useCurrentCompany()
  const { data: financials } = useCompanyControllerGetFinancialsQuery(
    { id: currentCompany?.id ?? "" },
    { skip: !currentCompany?.id },
  )
  const weekStartDay = useMemo(
    () => resolveWeekStartDay(financials),
    [financials],
  )

  // Same window construction as the dashboard (buildKpiWindows), reduced to
  // epoch-ms primitives so fresh-but-equal Date instances cannot churn the
  // query identity. No params → the fixed trailing window per granularity.
  const dateRangeParam = params?.dateRange
  const compareRangeParam = params?.compareRange
  const rangeStartMs = dateRangeParam ? dateRangeParam[0].getTime() : null
  const rangeEndMs = dateRangeParam ? dateRangeParam[1].getTime() : null
  const compareStartMs = Array.isArray(compareRangeParam)
    ? compareRangeParam[0].getTime()
    : null
  const compareEndMs = Array.isArray(compareRangeParam)
    ? compareRangeParam[1].getTime()
    : null
  const windows = useMemo(
    () =>
      buildKpiWindows(granularity, {
        range:
          rangeStartMs !== null && rangeEndMs !== null
            ? [new Date(rangeStartMs), new Date(rangeEndMs)]
            : undefined,
        compare:
          compareStartMs !== null && compareEndMs !== null
            ? [new Date(compareStartMs), new Date(compareEndMs)]
            : undefined,
      }),
    [granularity, rangeStartMs, rangeEndMs, compareStartMs, compareEndMs],
  )
  const weeklyKeepCount = weeklyBucketCount(windows.currentRange)

  // Bucket pairing: calendar-YoY keys on the default path, offset-from-
  // window-start keys for an explicit compare window (see useConnect).
  const comparisonKeyOf = useCallback(
    (dateStr: string, window: YoyWindow) => {
      const cubeGranularity = GRANULARITY_SUFFIX[granularity] as YoyGranularity
      if (windows.compareMode === "window") {
        return getWindowComparisonKey(
          dateStr,
          cubeGranularity,
          weekStartDay,
          window,
          {
            currentStart: windows.currentRange[0],
            comparisonStart: windows.comparisonRange[0],
          },
        )
      }
      return getYoyComparisonKey(dateStr, cubeGranularity, weekStartDay, window)
    },
    [granularity, weekStartDay, windows],
  )
  const comparisonStartOf = useMemo(
    () =>
      windows.compareMode === "window"
        ? comparisonShiftForWindows(
            GRANULARITY_SUFFIX[granularity],
            windows.currentRange[0],
            windows.comparisonRange[0],
            weekStartDay,
          )
        : undefined,
    [windows, granularity, weekStartDay],
  )

  // The regional snapshot only exists for opted-in companies after a pipeline
  // run; until then the capabilities gate skips the query, and the view says
  // so instead of showing empty tables.
  const { isLoaded: availabilityLoaded, isAvailable } =
    useCubeAvailability(REGIONAL_CUBE)

  const query = useMemo(
    () => buildRegionalQuery(country, granularity, windows),
    [country, granularity, windows],
  )
  const { resultSet, isLoading } = useCubeQuery(query)

  const dataSource = useMemo((): RegionalKpiRow[] | null => {
    const responses = (
      resultSet as unknown as {
        loadResponses?: Array<{ data?: RegionalKpiRow[] }>
      } | null
    )?.loadResponses
    if (!responses || responses.length === 0) return null

    const rawCurrent = responses[1]?.data ?? []
    const rawComparison = responses[0]?.data ?? []
    const dateKey = resolveRegionalDateKey(
      rawCurrent.length > 0 ? rawCurrent : rawComparison,
      granularity,
    )
    const merged = mergeCompareWindows(
      [...rawCurrent].sort(byDate(dateKey)),
      [...rawComparison].sort(byDate(dateKey)),
      dateKey,
      comparisonKeyOf,
    ) as RegionalKpiRow[]
    // Default weekly windows cover 53 buckets (oldest partial); keep the most
    // recent `weeklyKeepCount` exactly like the dashboard datasets.
    return granularity === AggregationType.WEEKLY
      ? merged.slice(-weeklyKeepCount)
      : merged
  }, [resultSet, granularity, comparisonKeyOf, weeklyKeepCount])

  const dateKey = useMemo(
    () => resolveRegionalDateKey(dataSource, granularity),
    [dataSource, granularity],
  )

  const processMetricsData = useCallback(
    (metrics: MetricDefinition[]) =>
      processRegionalMetrics(dataSource, dateKey, metrics),
    [dataSource, dateKey],
  )

  const columnConfig = useMemo(
    () =>
      buildRegionalColumns(
        dataSource,
        dateKey,
        granularity,
        dayjs,
        comparisonStartOf,
      ),
    [dataSource, dateKey, granularity, dayjs, comparisonStartOf],
  )

  return {
    isLoading,
    /** False once db_capabilities confirms the regional table is missing. */
    isAvailable,
    availabilityLoaded,
    processMetricsData,
    columnConfig,
    windows,
  }
}
